import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DivisionWorkflowService } from '../services/division-workflow-service';
import httpClient from '../services/http-client';
import { 
  WorkflowListResponse,
  WorkflowDetailResponse,
  CreateWorkflowRequest,
  UpdateWorkflowRequest,
  ExecuteWorkflowRequest,
  WorkflowExecutionResponse,
  WorkflowExecutionListResponse,
  WorkflowExecutionDetailResponse
} from '../types';

// Create a division workflow service instance
const divisionWorkflowService = new DivisionWorkflowService(httpClient);

// Query keys for division workflows
export const divisionWorkflowQueryKeys = {
  all: ['division-workflows'] as const,
  lists: () => [...divisionWorkflowQueryKeys.all, 'list'] as const,
  list: (divisionId: string, filters: Record<string, any>) => 
    [...divisionWorkflowQueryKeys.lists(), divisionId, filters] as const,
  details: () => [...divisionWorkflowQueryKeys.all, 'detail'] as const,
  detail: (divisionId: string, id: string) => 
    [...divisionWorkflowQueryKeys.details(), divisionId, id] as const,
  executions: () => [...divisionWorkflowQueryKeys.all, 'executions'] as const,
  executionsList: (divisionId: string, workflowId: string, filters: Record<string, any>) =>
    [...divisionWorkflowQueryKeys.executions(), divisionId, workflowId, filters] as const,
  executionDetail: (divisionId: string, workflowId: string, executionId: string) =>
    [...divisionWorkflowQueryKeys.executions(), divisionId, workflowId, executionId] as const,
  approverReport: (divisionId: string, jsonPath: string, filters: Record<string, any>) =>
    [...divisionWorkflowQueryKeys.all, 'approver-report', divisionId, jsonPath, filters] as const,
  types: (divisionId: string) =>
    [...divisionWorkflowQueryKeys.all, 'types', divisionId] as const,
  userWorkflows: (divisionId: string, userId: string) =>
    [...divisionWorkflowQueryKeys.all, 'user-workflows', divisionId, userId] as const,
};

/**
 * Hook to get all workflows for a specific division
 */
export function useDivisionWorkflows(
  divisionId: string,
  params?: {
    active?: boolean;
    type?: string;
    matchingMode?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    page?: number;
    limit?: number;
  },
  enabled = true
) {
  return useQuery({
    queryKey: divisionWorkflowQueryKeys.list(divisionId, params || {}),
    queryFn: () => divisionWorkflowService.getWorkflows(divisionId, params),
    enabled: enabled && !!divisionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to get a specific workflow in a division
 */
export function useDivisionWorkflow(
  divisionId: string, 
  workflowId: string, 
  enabled = true
) {
  return useQuery({
    queryKey: divisionWorkflowQueryKeys.detail(divisionId, workflowId),
    queryFn: () => divisionWorkflowService.getWorkflow(divisionId, workflowId),
    enabled: enabled && !!divisionId && !!workflowId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to create a new workflow in a division
 */
export function useCreateDivisionWorkflow(divisionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateWorkflowRequest) =>
      divisionWorkflowService.createWorkflow(divisionId, request),
    onSuccess: (data) => {
      // Invalidate and refetch workflows list for this division
      queryClient.invalidateQueries({ 
        queryKey: divisionWorkflowQueryKeys.lists() 
      });
      
      // Add the new workflow to the cache
      queryClient.setQueryData(
        divisionWorkflowQueryKeys.detail(divisionId, data.id),
        data
      );
      
      // Invalidate users cache in case new users were created via email invitations
      queryClient.invalidateQueries({ 
        queryKey: ['division-users', 'list']
      });
    },
  });
}

/**
 * Hook to update an existing workflow in a division
 */
export function useUpdateDivisionWorkflow(divisionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workflowId, request }: { 
      workflowId: string; 
      request: UpdateWorkflowRequest;
    }) => divisionWorkflowService.updateWorkflow(divisionId, workflowId, request),
    onSuccess: (data, variables) => {
      // Update the specific workflow in cache
      queryClient.setQueryData(
        divisionWorkflowQueryKeys.detail(divisionId, variables.workflowId),
        data
      );
      
      // Invalidate workflows list to reflect changes
      queryClient.invalidateQueries({ 
        queryKey: divisionWorkflowQueryKeys.lists() 
      });
      
      // Invalidate users cache in case new users were created via email invitations
      queryClient.invalidateQueries({ 
        queryKey: ['division-users', 'list']
      });
    },
  });
}

/**
 * Hook to delete a workflow from a division
 */
export function useDeleteDivisionWorkflow(divisionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (workflowId: string) =>
      divisionWorkflowService.deleteWorkflow(divisionId, workflowId),
    onSuccess: (_, workflowId) => {
      // Remove the workflow from cache
      queryClient.removeQueries({ 
        queryKey: divisionWorkflowQueryKeys.detail(divisionId, workflowId) 
      });
      
      // Invalidate workflows list
      queryClient.invalidateQueries({ 
        queryKey: divisionWorkflowQueryKeys.lists() 
      });
    },
  });
}

/**
 * Hook to clone a workflow in a division
 */
export function useCloneDivisionWorkflow(divisionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workflowId, request }: { 
      workflowId: string; 
      request?: {
        name?: string;
        active?: boolean;
        includeMatchCriteria?: boolean;
      };
    }) => divisionWorkflowService.cloneWorkflow(divisionId, workflowId, request),
    onSuccess: (data) => {
      // Add the cloned workflow to cache
      queryClient.setQueryData(
        divisionWorkflowQueryKeys.detail(divisionId, data.id),
        data
      );
      
      // Invalidate workflows list to show the new clone
      queryClient.invalidateQueries({ 
        queryKey: divisionWorkflowQueryKeys.lists() 
      });
    },
  });
}

/**
 * Hook to execute a workflow in a division
 */
export function useExecuteDivisionWorkflow(divisionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workflowId, request }: { 
      workflowId: string; 
      request: ExecuteWorkflowRequest;
    }) => divisionWorkflowService.executeWorkflow(divisionId, workflowId, request),
    onSuccess: (_, variables) => {
      // Invalidate workflow executions to show the new execution
      queryClient.invalidateQueries({ 
        queryKey: divisionWorkflowQueryKeys.executions() 
      });
    },
  });
}

/**
 * Hook to execute a workflow using Temporal orchestration
 */
export function useExecuteTemporalWorkflow(divisionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workflowId, request }: { 
      workflowId: string; 
      request: ExecuteWorkflowRequest;
    }) => divisionWorkflowService.executeTemporalWorkflow(divisionId, workflowId, request),
    onSuccess: (_, variables) => {
      // Invalidate workflow executions to show the new execution
      queryClient.invalidateQueries({ 
        queryKey: divisionWorkflowQueryKeys.executions() 
      });
    },
  });
}

/**
 * Hook to get workflow executions for a specific workflow in a division
 */
export function useDivisionWorkflowExecutions(
  divisionId: string,
  workflowId: string,
  params?: {
    status?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  },
  enabled = true
) {
  return useQuery({
    queryKey: divisionWorkflowQueryKeys.executionsList(divisionId, workflowId, params || {}),
    queryFn: () => divisionWorkflowService.getWorkflowExecutions(divisionId, workflowId, params),
    enabled: enabled && !!divisionId && !!workflowId,
    staleTime: 30 * 1000, // 30 seconds (executions change more frequently)
  });
}

/**
 * Hook to get a specific workflow execution detail
 */
export function useDivisionWorkflowExecution(
  divisionId: string,
  workflowId: string,
  executionId: string,
  enabled = true
) {
  return useQuery({
    queryKey: divisionWorkflowQueryKeys.executionDetail(divisionId, workflowId, executionId),
    queryFn: () => divisionWorkflowService.getWorkflowExecution(divisionId, workflowId, executionId),
    enabled: enabled && !!divisionId && !!workflowId && !!executionId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to import workflows from CSV
 */
export function useImportDivisionWorkflows(divisionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: {
      file: File;
      options?: {
        updateExisting?: boolean;
        createUsers?: boolean;
        routingRuleField?: string;
        templateId?: string;
        applyTemplateAttributes?: boolean;
      };
    }) =>
      divisionWorkflowService.importWorkflows(
        divisionId,
        params.file,
        params.options || {}
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: divisionWorkflowQueryKeys.lists(),
      });
    },
  });
}

/**
 * Hook to get the admin "approvers by routing rule" report for a division.
 */
export function useWorkflowApproverReport(
  divisionId: string,
  jsonPath: string,
  filters?: { type?: string; nameContains?: string },
  enabled = true
) {
  return useQuery({
    queryKey: divisionWorkflowQueryKeys.approverReport(divisionId, jsonPath, filters || {}),
    queryFn: () => divisionWorkflowService.getApproverReport(divisionId, jsonPath, filters),
    enabled: enabled && !!divisionId && !!jsonPath,
    staleTime: 60 * 1000,
  });
}

/**
 * Hook to get the distinct `Type` values actually in use for a division's workflows.
 */
export function useDivisionWorkflowTypes(divisionId: string, enabled = true) {
  return useQuery({
    queryKey: divisionWorkflowQueryKeys.types(divisionId),
    queryFn: () => divisionWorkflowService.getWorkflowTypes(divisionId),
    enabled: enabled && !!divisionId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to list the workflows a given user is a configured approver on.
 */
export function useUserWorkflowAssociations(divisionId: string, userId: string, enabled = true) {
  return useQuery({
    queryKey: divisionWorkflowQueryKeys.userWorkflows(divisionId, userId),
    queryFn: () => divisionWorkflowService.getWorkflowsForUser(divisionId, userId),
    enabled: enabled && !!divisionId && !!userId,
    staleTime: 60 * 1000,
  });
}


